import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Video, Sparkles, Loader2, Plus, Trash2, Check, Send, PenLine, ListChecks,
  Users, MapPin, Repeat, ArrowRight, ArrowLeft, Wand2, Camera, Upload, FolderOpen, Rocket, Smartphone, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import BuilderTopBar from '@/components/creation-flow/BuilderTopBar';
import { BuilderCard, builderLabelStyle, builderHelpStyle } from '@/components/creation-flow/BuilderCard';
import { RecordingModal } from '@/components/recording/RecordingModal';
import VideoEditDrawer from '@/components/video-editor/VideoEditDrawer';
import { ToastAction } from '@/components/ui/toast';
import {
  fetchSeriesProjects, fetchSeriesProject, createSeriesProject, updateSeriesProject, deleteSeriesProject,
  seriesDevelop, seriesPlan, seriesStructure, seriesChat, seriesDraft,
  getProductFileUploadUrl, requestMuxIngest, createProduct, updateProduct,
  type SeriesProject, type SeriesEpisode, type ConciergeChatMessage, type ProductVideo, type CreatorProduct,
} from '@/services/creatorApi';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

type Phase = 'pitch' | 'plan' | 'write' | 'prep' | 'produce';
const PHASES: { key: Phase; label: string }[] = [
  { key: 'pitch', label: 'Pitch' },
  { key: 'plan', label: 'Plan' },
  { key: 'write', label: 'Write' },
  { key: 'prep', label: 'Prep' },
  { key: 'produce', label: 'Produce' },
];
const FORMATS: { key: SeriesProject['format']; label: string }[] = [
  { key: 'pov', label: 'POV / monologue' },
  { key: 'sketch', label: 'Sketch' },
  { key: 'mockumentary', label: 'Mockumentary' },
  { key: 'recurring-bit', label: 'Recurring bit' },
];
const genId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
const blankProject = (): SeriesProject => ({
  title: '', premise: '', format: '', tone: '', aspect: '9:16',
  characters: [], locations: [], runningBits: [], assets: [], episodes: [],
});

const SeriesStudioPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const prefill = (location.state || {}) as { description?: string; title?: string };

  const [project, setProject] = useState<SeriesProject>(blankProject());
  const [phase, setPhase] = useState<Phase>('pitch');
  const [writeEpisodeId, setWriteEpisodeId] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!projectId);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const initialLoad = useRef(true);

  // Landing (no project yet)
  const [projects, setProjects] = useState<SeriesProject[]>([]);
  const [idea, setIdea] = useState(prefill.description || prefill.title || '');
  const [developing, setDeveloping] = useState(false);

  // Load an existing project, or the list of drafts.
  useEffect(() => {
    if (projectId) {
      setLoading(true);
      fetchSeriesProject(projectId)
        .then(p => { setProject(p); })
        .catch(() => { toast({ title: 'Project not found', variant: 'destructive' }); navigate('/my-store/series-studio'); })
        .finally(() => { setLoading(false); initialLoad.current = true; setDirty(false); });
    } else {
      fetchSeriesProjects().then(setProjects).catch(() => {});
    }
  }, [projectId]);

  useEffect(() => {
    if (initialLoad.current) { initialLoad.current = false; return; }
    setDirty(true);
  }, [project]);

  const patch = useCallback((p: Partial<SeriesProject>) => setProject(prev => ({ ...prev, ...p })), []);

  const save = useCallback(async (): Promise<SeriesProject | null> => {
    if (!projectId) return null;
    setSaving(true);
    try {
      const saved = await updateSeriesProject(projectId, project);
      setDirty(false);
      return saved;
    } catch (err: any) {
      toast({ title: 'Save failed', description: err.message, variant: 'destructive' });
      return null;
    } finally { setSaving(false); }
  }, [projectId, project]);

  // --- Landing: develop from an idea, or start blank ---
  const developFromIdea = async () => {
    if (!idea.trim()) return;
    setDeveloping(true);
    try {
      const bible = await seriesDevelop(idea.trim());
      const draft: SeriesProject = {
        ...blankProject(),
        title: bible.titleOptions[0] || idea.trim().slice(0, 60),
        premise: bible.premise,
        format: bible.format,
        tone: bible.tone,
        characters: bible.characters.map(c => ({ ...c, id: genId() })),
        locations: bible.locations.map(l => ({ ...l, id: genId() })),
        runningBits: bible.runningBits,
      };
      const created = await createSeriesProject(draft);
      toast({ title: bible.premise ? 'Series drafted' : 'Project started' });
      navigate(`/my-store/series-studio/${created._id}`);
    } catch (err: any) {
      toast({ title: 'Could not start', description: err.message, variant: 'destructive' });
    } finally { setDeveloping(false); }
  };

  const startBlank = async () => {
    setDeveloping(true);
    try {
      const created = await createSeriesProject({ ...blankProject(), title: 'Untitled series' });
      navigate(`/my-store/series-studio/${created._id}`);
    } catch (err: any) {
      toast({ title: 'Could not start', description: err.message, variant: 'destructive' });
    } finally { setDeveloping(false); }
  };

  if (!projectId) {
    return (
      <StudioLanding
        idea={idea} setIdea={setIdea} developing={developing}
        onDevelop={developFromIdea} onBlank={startBlank}
        projects={projects} onOpen={p => navigate(`/my-store/series-studio/${p._id}`)}
        onDelete={async (id) => { await deleteSeriesProject(id); setProjects(prev => prev.filter(p => p._id !== id)); }}
      />
    );
  }

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center" style={{ background: 'var(--hj-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--hj-ink-3)' }} />
      </main>
    );
  }

  const epCount = project.episodes.length;
  const scriptedCount = project.episodes.filter(e => (e.script || '').trim()).length;
  const meta = `${FORMATS.find(f => f.key === project.format)?.label || 'Series'} · ${epCount} ep${epCount !== 1 ? 's' : ''}${scriptedCount ? ` · ${scriptedCount} scripted` : ''}`;

  return (
    <main className="min-h-screen" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <BuilderTopBar
        kicker="Series studio"
        kickerIcon={Video}
        kickerColor="var(--hj-deep-blue)"
        title={project.title}
        titlePlaceholder="Untitled series"
        meta={meta}
        onBack={async () => { if (dirty) await save(); navigate('/my-store'); }}
        saving={saving}
        dirty={dirty}
        saveLabel="Save"
        onSave={save}
      />

      <div className="mx-auto" style={{ maxWidth: 1120, padding: '20px 16px 96px' }}>
        {/* Phase stepper */}
        <div className="flex items-center gap-1.5 flex-wrap" style={{ marginBottom: 20 }}>
          {PHASES.map((p, i) => (
            <React.Fragment key={p.key}>
              <button
                type="button"
                onClick={() => setPhase(p.key)}
                className="inline-flex items-center gap-1.5"
                style={{ padding: '7px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', border: `1px solid ${phase === p.key ? 'var(--hj-blue)' : 'var(--hj-hairline)'}`, background: phase === p.key ? 'var(--hj-blue)' : 'transparent', color: phase === p.key ? 'oklch(0.18 0.005 70)' : 'var(--hj-ink-3)' }}
              >
                <span className="tabular" style={{ opacity: 0.7 }}>{i + 1}</span> {p.label}
              </button>
              {i < PHASES.length - 1 && <ArrowRight className="w-3.5 h-3.5" style={{ color: 'var(--hj-ink-4)' }} />}
            </React.Fragment>
          ))}
        </div>

        {phase === 'pitch' && <PitchPhase project={project} patch={patch} onNext={() => setPhase('plan')} />}
        {phase === 'plan' && <PlanPhase project={project} patch={patch} onWrite={(id) => { setPhase('write'); setWriteEpisodeId(id); }} />}
        {phase === 'write' && <WritePhase project={project} patch={patch} initialEpisodeId={writeEpisodeId} />}
        {phase === 'prep' && <PrepPhase project={project} patch={patch} />}
        {phase === 'produce' && <ProducePhase project={project} patch={patch} projectId={projectId!} onPersist={updateSeriesProject} />}
      </div>
    </main>
  );
};

// ============================ Landing ============================
function StudioLanding({ idea, setIdea, developing, onDevelop, onBlank, projects, onOpen, onDelete }: {
  idea: string; setIdea: (s: string) => void; developing: boolean;
  onDevelop: () => void; onBlank: () => void;
  projects: SeriesProject[]; onOpen: (p: SeriesProject) => void; onDelete: (id: string) => void;
}) {
  const navigate = useNavigate();
  return (
    <main className="min-h-screen" style={{ background: 'var(--hj-bg)', color: 'var(--hj-ink)' }}>
      <div className="mx-auto" style={{ maxWidth: 760, padding: '48px 16px 96px' }}>
        <button type="button" onClick={() => navigate('/my-store')} className="inline-flex items-center gap-1.5" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-ink-3)', fontSize: 13, marginBottom: 20 }}>
          <ArrowLeft className="w-4 h-4" /> My Store
        </button>
        <div className="mono-label flex items-center gap-1.5" style={{ color: 'var(--hj-ink-3)' }}><Video className="w-3.5 h-3.5" style={{ color: 'var(--hj-deep-blue)' }} /> Series studio</div>
        <h1 className="font-hj-display m-0" style={{ fontSize: 30, letterSpacing: '-0.02em', fontWeight: 800, marginTop: 6 }}>Develop a series</h1>
        <p className="m-0" style={{ marginTop: 8, fontSize: 15, color: 'var(--hj-ink)', lineHeight: 1.5 }}>
          Vertical, phone-shot, comedy-leaning. Tell us the idea — we'll draft the characters, the season, and the beats. You write the funny.
        </p>

        <BuilderCard style={{ marginTop: 24 }}>
          <div>
            <Label style={builderLabelStyle}>What's the series about?</Label>
            <Textarea value={idea} onChange={e => setIdea(e.target.value)} rows={3} placeholder="e.g. A deadpan barista who narrates every customer like a nature documentary." />
            <p className="m-0" style={builderHelpStyle}>One line is plenty. We'll fill in the rest and you can change anything.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 4 }}>
            <button type="button" onClick={onDevelop} disabled={!idea.trim() || developing} className="inline-flex items-center gap-1.5" style={{ padding: '10px 18px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', opacity: (!idea.trim() || developing) ? 0.6 : 1, minHeight: 44 }}>
              {developing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Develop with AI
            </button>
            <button type="button" onClick={onBlank} disabled={developing} style={{ padding: '10px 18px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 13, fontWeight: 600, cursor: 'pointer', minHeight: 44 }}>
              Start blank
            </button>
          </div>
        </BuilderCard>

        {projects.length > 0 && (
          <div style={{ marginTop: 32 }}>
            <div className="mono-label" style={{ color: 'var(--hj-ink-3)', marginBottom: 10 }}>In development</div>
            <div className="flex flex-col gap-2">
              {projects.map(p => (
                <div key={p._id} className="flex items-center gap-3" style={{ padding: 14, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-surface-1)', border: '1px solid var(--hj-hairline)' }}>
                  <button type="button" onClick={() => onOpen(p)} className="flex-1 text-left" style={{ background: 'none', border: 0, cursor: 'pointer', minWidth: 0 }}>
                    <div className="truncate" style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--hj-ink)' }}>{p.title || 'Untitled series'}</div>
                    <div style={{ fontSize: 12.5, color: 'var(--hj-ink-3)', marginTop: 2 }}>{p.episodes?.length || 0} episodes · {(p.characters?.length || 0)} characters</div>
                  </button>
                  <button type="button" onClick={() => onOpen(p)} className="inline-flex items-center gap-1" style={{ padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>Open <ArrowRight className="w-3.5 h-3.5" /></button>
                  <button type="button" onClick={() => { if (window.confirm('Delete this series project?')) onDelete(p._id!); }} aria-label="Delete" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

// ============================ Pitch ============================
function PitchPhase({ project, patch, onNext }: { project: SeriesProject; patch: (p: Partial<SeriesProject>) => void; onNext: () => void }) {
  const { toast } = useToast();
  const [reidea, setReidea] = useState('');
  const [busy, setBusy] = useState(false);
  const regenerate = async () => {
    if (!reidea.trim()) return;
    setBusy(true);
    try {
      const b = await seriesDevelop(reidea.trim());
      // Empty scaffold = the AI couldn't be reached. Never wipe the existing
      // bible with blanks — just tell the user and leave their work intact.
      if (!b.premise && !b.titleOptions.length && !b.characters.length && !b.locations.length) {
        toast({ title: 'Couldn’t redraft', description: 'The writers’ room didn’t answer — try again in a moment. Your bible is untouched.', variant: 'destructive' });
        return;
      }
      patch({
        title: b.titleOptions[0] || project.title,
        premise: b.premise || project.premise,
        format: b.format || project.format,
        tone: b.tone || project.tone,
        // Only replace lists the AI actually returned; otherwise keep what's there.
        characters: b.characters.length ? b.characters.map(c => ({ ...c, id: genId() })) : project.characters,
        locations: b.locations.length ? b.locations.map(l => ({ ...l, id: genId() })) : project.locations,
        runningBits: b.runningBits.length ? b.runningBits : project.runningBits,
      });
    } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
      <div className="w-full lg:w-80 lg:flex-shrink-0 flex flex-col gap-5">
        <BuilderCard kicker="The bible">
          <div>
            <Label style={builderLabelStyle}>Title</Label>
            <Input value={project.title} onChange={e => patch({ title: e.target.value })} placeholder="Series title" />
          </div>
          <div>
            <Label style={builderLabelStyle}>Premise</Label>
            <Textarea value={project.premise || ''} onChange={e => patch({ premise: e.target.value })} rows={3} placeholder="What's the show, in a line or two?" />
          </div>
          <div>
            <Label style={builderLabelStyle}>Format</Label>
            <div className="flex gap-1.5 flex-wrap">
              {FORMATS.map(f => (
                <button key={f.key} type="button" onClick={() => patch({ format: f.key })} style={{ padding: '6px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${project.format === f.key ? '#ffd600' : 'var(--hj-beige-line)'}`, background: project.format === f.key ? 'var(--hj-yellow-tint)' : '#fff', color: 'var(--hj-beige-ink)' }}>{f.label}</button>
              ))}
            </div>
          </div>
          <div>
            <Label style={builderLabelStyle}>Tone</Label>
            <Input value={project.tone || ''} onChange={e => patch({ tone: e.target.value })} placeholder="deadpan · unhinged · wholesome chaos" />
          </div>
        </BuilderCard>

        <BuilderCard kicker="Redraft with AI">
          <p className="m-0" style={builderHelpStyle}>Not feeling it? Describe a new angle and we'll redraft the bible.</p>
          <Textarea value={reidea} onChange={e => setReidea(e.target.value)} rows={2} placeholder="New idea or a tweak…" />
          <button type="button" onClick={regenerate} disabled={!reidea.trim() || busy} className="inline-flex items-center gap-1.5 justify-center w-full" style={{ padding: '9px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: (!reidea.trim() || busy) ? 0.6 : 1 }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Redraft
          </button>
        </BuilderCard>
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-5">
        <EditableList
          icon={Users} title="Characters"
          items={project.characters}
          onChange={(characters) => patch({ characters })}
          blank={() => ({ id: genId(), name: '', description: '', playedBy: '' })}
          render={(c, update) => (
            <>
              <Input value={c.name} onChange={e => update({ name: e.target.value })} placeholder="Name" className="h-9" />
              <Input value={c.description || ''} onChange={e => update({ description: e.target.value })} placeholder="One line about them" className="h-9" />
              <Input value={c.playedBy || ''} onChange={e => update({ playedBy: e.target.value })} placeholder="Played by (me / a friend / name)" className="h-9" style={{ maxWidth: 220 }} />
            </>
          )}
        />
        <EditableList
          icon={MapPin} title="Locations"
          items={project.locations}
          onChange={(locations) => patch({ locations })}
          blank={() => ({ id: genId(), name: '', description: '' })}
          render={(l, update) => (
            <>
              <Input value={l.name} onChange={e => update({ name: e.target.value })} placeholder="Location (my kitchen, a parked car…)" className="h-9" />
              <Input value={l.description || ''} onChange={e => update({ description: e.target.value })} placeholder="Note (optional)" className="h-9" />
            </>
          )}
        />
        <BuilderCard>
          <div className="flex items-center gap-2" style={{ marginBottom: 8 }}>
            <Repeat className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-3)' }} />
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Running bits</span>
          </div>
          <StringList items={project.runningBits} onChange={(runningBits) => patch({ runningBits })} placeholder="A recurring gag to escalate…" />
        </BuilderCard>

        <div className="flex justify-end">
          <button type="button" onClick={onNext} className="inline-flex items-center gap-1.5" style={{ padding: '11px 20px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer' }}>
            Plan the season <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================ Plan ============================
function PlanPhase({ project, patch, onWrite }: { project: SeriesProject; patch: (p: Partial<SeriesProject>) => void; onWrite: (episodeId: string) => void }) {
  const { toast } = useToast();
  const [count, setCount] = useState(8);
  const [busy, setBusy] = useState(false);
  const planSeason = async () => {
    setBusy(true);
    try {
      const { episodes, code } = await seriesPlan(project, count);
      if (episodes.length) {
        patch({ episodes: episodes.map((e, i) => ({ id: genId(), number: e.number || i + 1, title: e.title, logline: e.logline, beats: e.beats, hookOptions: [], script: '', shotList: [], status: 'planned' as const })) });
      } else if (code === 'SERIES_TIMEOUT') {
        // A timeout used to be reported as "try again in a moment", which was the
        // wrong advice: it timed out every time, and people retried until they
        // gave up. Say what happened and offer the size that will actually finish.
        toast({
          title: 'The season took too long to write',
          description: count > 6
            ? `Try ${Math.min(6, count - 2)} episodes instead — a shorter season comes back faster. You can always add more after.`
            : 'Give it one more go, or add episodes yourself with “Add”.',
          variant: 'destructive',
        });
      } else {
        toast({ title: 'Couldn’t reach the writers’ room', description: 'Add episodes yourself with “Add”, and we’ll pick it up from there.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Couldn’t plan the season', description: 'Something went wrong — try again, or add episodes yourself.', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  const addEpisode = () => patch({ episodes: [...project.episodes, { id: genId(), number: project.episodes.length + 1, title: '', logline: '', beats: [], hookOptions: [], script: '', shotList: [], status: 'planned' }] });
  const updateEpisode = (id: string, u: Partial<SeriesEpisode>) => patch({ episodes: project.episodes.map(e => e.id === id ? { ...e, ...u } : e) });
  const removeEpisode = (id: string) => patch({ episodes: project.episodes.filter(e => e.id !== id).map((e, i) => ({ ...e, number: i + 1 })) });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h2 className="font-hj-display m-0" style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Episodes</h2>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="flex items-center gap-1.5" style={{ fontSize: 12.5, color: 'var(--hj-ink)' }}>
            How many
            <input type="number" min={1} max={16} value={count} onChange={e => setCount(Math.max(1, Math.min(16, parseInt(e.target.value, 10) || 1)))} className="tabular text-center" style={{ width: 52, height: 34, borderRadius: 8, border: '1px solid var(--hj-hairline)', background: 'var(--hj-surface-1)', color: 'var(--hj-ink)', fontFamily: 'var(--hj-font-mono)' }} />
          </label>
          <button type="button" onClick={planSeason} disabled={busy} className="inline-flex items-center gap-1.5" style={{ padding: '8px 15px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Plan a season
          </button>
          <button type="button" onClick={addEpisode} className="inline-flex items-center gap-1.5" style={{ padding: '8px 15px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
      </div>

      {project.episodes.length === 0 ? (
        <div className="text-center" style={{ padding: '48px 24px', borderRadius: 'var(--hj-r-md)', border: '1.5px dashed var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14 }}>
          Plan a season with AI, or add episodes yourself.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {project.episodes.map((e) => (
            <div key={e.id} data-paper="beige" style={{ padding: 14, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
              <div className="flex items-center gap-3">
                <span className="tabular flex-shrink-0" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}>Ep {e.number}</span>
                <Input value={e.title} onChange={ev => updateEpisode(e.id!, { title: ev.target.value })} placeholder="Episode title" className="h-9 flex-1" />
                <StatusChip status={e.status} scripted={!!(e.script || '').trim()} />
                <button type="button" onClick={() => onWrite(e.id!)} className="inline-flex items-center gap-1 flex-shrink-0" style={{ padding: '6px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}><PenLine className="w-3.5 h-3.5" /> Write</button>
                <button type="button" onClick={() => removeEpisode(e.id!)} aria-label="Remove" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
              </div>
              <Input value={e.logline || ''} onChange={ev => updateEpisode(e.id!, { logline: ev.target.value })} placeholder="Logline" className="h-8" style={{ marginTop: 8, fontSize: 12.5 }} />
              {(e.beats?.length || 0) > 0 && (
                <ul className="m-0" style={{ marginTop: 8, paddingLeft: 18, fontSize: 12.5, color: 'var(--hj-beige-ink-2)' }}>
                  {e.beats!.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ Write ============================
function WritePhase({ project, patch, initialEpisodeId }: { project: SeriesProject; patch: (p: Partial<SeriesProject>) => void; initialEpisodeId: string | null }) {
  const { toast } = useToast();
  const [activeId, setActiveId] = useState<string | null>(initialEpisodeId || project.episodes[0]?.id || null);
  const ep = project.episodes.find(e => e.id === activeId) || null;
  const [busy, setBusy] = useState<'structure' | 'draft' | null>(null);
  const [chat, setChat] = useState<ConciergeChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatBusy, setChatBusy] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setChat([]); }, [activeId]);
  useEffect(() => { chatRef.current?.scrollTo(0, chatRef.current.scrollHeight); }, [chat, chatBusy]);

  const updateEp = (u: Partial<SeriesEpisode>) => { if (ep) patch({ episodes: project.episodes.map(e => e.id === ep.id ? { ...e, ...u } : e) }); };

  const getStructure = async () => {
    if (!ep) return;
    setBusy('structure');
    try {
      const s = await seriesStructure(project, ep);
      // Nothing back = AI unreachable. Don't clobber existing structure with blanks.
      if (!s.beats.length && !s.hookOptions.length && !s.shotList.length) {
        toast({ title: 'Couldn’t reach the writers’ room', description: 'Try again in a moment, or write the beats yourself.', variant: 'destructive' });
        return;
      }
      updateEp({
        beats: s.beats.length ? s.beats : ep.beats,
        hookOptions: s.hookOptions.length ? s.hookOptions : ep.hookOptions,
        shotList: s.shotList.length ? s.shotList : ep.shotList,
      });
    } catch {
      toast({ title: 'Couldn’t get structure', description: 'Something went wrong — try again.', variant: 'destructive' });
    } finally { setBusy(null); }
  };
  const draftForMe = async () => {
    if (!ep) return;
    setBusy('draft');
    try {
      const { draft } = await seriesDraft(project, ep, ep.beats);
      if (draft) {
        const scaffold = `[AI SCAFFOLD — rewrite in your own voice]\n\n${draft}`;
        updateEp({ script: ep.script?.trim() ? ep.script : scaffold, status: ep.status === 'planned' ? 'scripted' : ep.status });
        toast({ title: 'Draft dropped in', description: 'It\'s a scaffold — make it yours.' });
      } else {
        toast({ title: 'Couldn’t draft it', description: 'The writers’ room didn’t answer — try again, or write it yourself.', variant: 'destructive' });
      }
    } catch {
      toast({ title: 'Couldn’t draft it', description: 'Something went wrong — try again.', variant: 'destructive' });
    } finally { setBusy(null); }
  };
  const sendChat = async () => {
    if (!chatInput.trim() || !ep) return;
    const next = [...chat, { role: 'user' as const, content: chatInput.trim() }];
    setChat(next); setChatInput(''); setChatBusy(true);
    try {
      const ctx = `Series: ${project.title} — ${project.premise} (${project.format}, ${project.tone}). Episode ${ep.number}: ${ep.title} — ${ep.logline}. Current draft:\n${(ep.script || '').slice(0, 1200)}`;
      const { reply } = await seriesChat(next, ctx);
      setChat([...next, { role: 'assistant', content: reply || "I'm here — tell me what to punch up." }]);
    } finally { setChatBusy(false); }
  };

  if (!project.episodes.length) {
    return <div className="text-center" style={{ padding: '48px 24px', borderRadius: 'var(--hj-r-md)', border: '1.5px dashed var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14 }}>Plan some episodes first.</div>;
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
      {/* Episode picker + structure */}
      <div className="w-full lg:w-72 lg:flex-shrink-0 flex flex-col gap-4">
        <BuilderCard kicker="Episode">
          <div className="flex flex-col gap-1">
            {project.episodes.map(e => (
              <button key={e.id} type="button" onClick={() => setActiveId(e.id!)} className="flex items-center gap-2 text-left" style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${activeId === e.id ? 'var(--hj-deep-blue)' : 'var(--hj-beige-line)'}`, background: activeId === e.id ? 'rgb(65 113 249 / 0.08)' : '#fff', cursor: 'pointer' }}>
                <span className="tabular flex-shrink-0" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>{e.number}</span>
                <span className="flex-1 truncate" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink)' }}>{e.title || 'Untitled'}</span>
                {(e.script || '').trim() && <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#5a8a2e' }} />}
              </button>
            ))}
          </div>
        </BuilderCard>

        {ep && (
          <BuilderCard kicker="Structure">
            <button type="button" onClick={getStructure} disabled={busy === 'structure'} className="inline-flex items-center gap-1.5 justify-center w-full" style={{ padding: '9px 14px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', opacity: busy === 'structure' ? 0.6 : 1 }}>
              {busy === 'structure' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Get beats & hooks
            </button>
            {(ep.hookOptions?.length || 0) > 0 && (
              <div>
                <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 4 }}>Cold-open hooks</div>
                <ul className="m-0" style={{ paddingLeft: 16, fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>{ep.hookOptions!.map((h, i) => <li key={i} style={{ marginBottom: 3 }}>{h}</li>)}</ul>
              </div>
            )}
            {(ep.beats?.length || 0) > 0 && (
              <div>
                <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 4 }}>Beats</div>
                <ul className="m-0" style={{ paddingLeft: 16, fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>{ep.beats!.map((b, i) => <li key={i} style={{ marginBottom: 3 }}>{b}</li>)}</ul>
              </div>
            )}
            {(ep.shotList?.length || 0) > 0 && (
              <div>
                <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 4 }}>Shot list</div>
                <ul className="m-0" style={{ paddingLeft: 16, fontSize: 12, color: 'var(--hj-beige-ink-2)' }}>{ep.shotList!.map((s, i) => <li key={i} style={{ marginBottom: 3 }}><strong>{s.framing}</strong> — {s.shot}{s.note ? ` (${s.note})` : ''}</li>)}</ul>
              </div>
            )}
          </BuilderCard>
        )}
      </div>

      {/* Script editor */}
      {ep && (
        <div className="flex-1 min-w-0 flex flex-col gap-4">
          <BuilderCard>
            <div className="flex items-center justify-between gap-2" style={{ marginBottom: 8 }}>
              <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>Ep {ep.number} — script <span style={{ fontWeight: 500, color: 'var(--hj-beige-ink-3)' }}>(you write the lines)</span></span>
              <button type="button" onClick={draftForMe} disabled={busy === 'draft'} className="inline-flex items-center gap-1.5" style={{ padding: '6px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-3)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', opacity: busy === 'draft' ? 0.6 : 1 }}>
                {busy === 'draft' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Draft it for me
              </button>
            </div>
            <Textarea value={ep.script || ''} onChange={e => updateEp({ script: e.target.value, status: ep.status === 'planned' && e.target.value.trim() ? 'scripted' : ep.status })} rows={16} placeholder="Write the episode — spoken lines, minimal action in (parens). Land a laugh in the first 2 seconds." style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 13, lineHeight: 1.6 }} />
          </BuilderCard>
        </div>
      )}

      {/* Writers' room chat */}
      {ep && (
        <div className="w-full lg:w-80 lg:flex-shrink-0">
          <BuilderCard kicker="Writers' room" style={{ display: 'flex', flexDirection: 'column', height: 520 }}>
            <div ref={chatRef} className="flex-1 overflow-y-auto flex flex-col gap-2" style={{ paddingRight: 4 }}>
              {chat.length === 0 && (
                <p className="m-0" style={{ ...builderHelpStyle, marginTop: 0 }}>Punch-up partner. Try "make the ending dumber", "5 alts for this line", or "give them a catchphrase."</p>
              )}
              {chat.map((m, i) => (
                <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '8px 11px', borderRadius: 12, fontSize: 12.5, lineHeight: 1.45, whiteSpace: 'pre-wrap', background: m.role === 'user' ? 'var(--hj-blue)' : '#fff', color: m.role === 'user' ? 'oklch(0.18 0.005 70)' : 'var(--hj-beige-ink)', border: m.role === 'user' ? 'none' : '1px solid var(--hj-beige-line)' }}>{m.content}</div>
              ))}
              {chatBusy && <div style={{ alignSelf: 'flex-start', color: 'var(--hj-beige-ink-3)', fontSize: 12 }}><Loader2 className="w-3.5 h-3.5 animate-spin inline" /> thinking…</div>}
            </div>
            <div className="flex items-end gap-1.5" style={{ marginTop: 10 }}>
              <Textarea value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendChat(); } }} rows={2} placeholder="Riff with the room…" style={{ fontSize: 12.5, resize: 'none' }} />
              <button type="button" onClick={sendChat} disabled={!chatInput.trim() || chatBusy} aria-label="Send" style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 999, background: 'var(--hj-blue)', border: 'none', color: 'oklch(0.18 0.005 70)', cursor: 'pointer', opacity: (!chatInput.trim() || chatBusy) ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Send className="w-4 h-4" /></button>
            </div>
          </BuilderCard>
        </div>
      )}
    </div>
  );
}

// ============================ Prep ============================
function PrepPhase({ project, patch }: { project: SeriesProject; patch: (p: Partial<SeriesProject>) => void }) {
  // Auto-compile a checklist from the bible (characters/locations) + any manual assets.
  const compile = () => {
    const items = [...project.assets];
    const has = (label: string) => items.some(a => a.label.toLowerCase() === label.toLowerCase());
    project.locations.forEach(l => { if (l.name && !has(`Secure: ${l.name}`)) items.push({ id: genId(), label: `Secure: ${l.name}`, kind: 'other', done: false }); });
    project.characters.forEach(c => { if (c.name && !has(`Cast: ${c.name}`)) items.push({ id: genId(), label: `Cast: ${c.name}${c.playedBy ? ` (${c.playedBy})` : ''}`, kind: 'wardrobe', done: false }); });
    patch({ assets: items });
  };
  const update = (id: string, u: Partial<SeriesProject['assets'][number]>) => patch({ assets: project.assets.map(a => a.id === id ? { ...a, ...u } : a) });
  const add = () => patch({ assets: [...project.assets, { id: genId(), label: '', kind: 'prop', done: false }] });
  const remove = (id: string) => patch({ assets: project.assets.filter(a => a.id !== id) });
  const doneCount = project.assets.filter(a => a.done).length;

  return (
    <div className="mx-auto" style={{ maxWidth: 720 }}>
      <div className="flex items-center justify-between gap-3 flex-wrap" style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-2">
          <ListChecks className="w-5 h-5" style={{ color: 'var(--hj-deep-blue)' }} />
          <h2 className="font-hj-display m-0" style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em' }}>Shoot prep</h2>
          {project.assets.length > 0 && <span className="mono-label" style={{ color: 'var(--hj-ink-3)' }}>{doneCount}/{project.assets.length}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={compile} className="inline-flex items-center gap-1.5" style={{ padding: '8px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}><Sparkles className="w-3.5 h-3.5" /> Compile from bible</button>
          <button type="button" onClick={add} className="inline-flex items-center gap-1.5" style={{ padding: '8px 14px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-hairline)', color: 'var(--hj-ink-2)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}><Plus className="w-3.5 h-3.5" /> Add</button>
        </div>
      </div>

      {project.assets.length === 0 ? (
        <div className="text-center" style={{ padding: '48px 24px', borderRadius: 'var(--hj-r-md)', border: '1.5px dashed var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14 }}>
          Compile a checklist from your characters and locations, or add items yourself.
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {project.assets.map(a => (
            <div key={a.id} data-paper="beige" className="flex items-center gap-3" style={{ padding: 12, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
              <button type="button" onClick={() => update(a.id!, { done: !a.done })} aria-label="Toggle done" className="flex-shrink-0 flex items-center justify-center" style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${a.done ? '#5a8a2e' : 'var(--hj-beige-line)'}`, background: a.done ? 'rgb(140 198 63 / 0.2)' : '#fff', cursor: 'pointer' }}>{a.done && <Check className="w-3.5 h-3.5" style={{ color: '#5a8a2e' }} />}</button>
              <Input value={a.label} onChange={e => update(a.id!, { label: e.target.value })} placeholder="Prop, wardrobe, location…" className="h-9 flex-1" style={{ textDecoration: a.done ? 'line-through' : 'none' }} />
              <select value={a.kind} onChange={e => update(a.id!, { kind: e.target.value as any })} className="h-9" style={{ borderRadius: 8, border: '1px solid var(--hj-beige-line)', background: '#fff', color: 'var(--hj-beige-ink)', fontSize: 12, padding: '0 6px' }}>
                <option value="prop">Prop</option>
                <option value="wardrobe">Wardrobe</option>
                <option value="other">Other</option>
              </select>
              <button type="button" onClick={() => remove(a.id!)} aria-label="Remove" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================ Produce & publish ============================
interface ProjectFile { id: string; name: string; s3Key: string; duration?: number; thumbnailUrl?: string; }

function ProducePhase({ project, patch, projectId, onPersist }: {
  project: SeriesProject; patch: (p: Partial<SeriesProject>) => void; projectId: string;
  onPersist: (id: string, p: Partial<SeriesProject>) => Promise<SeriesProject>;
}) {
  const { getAuthHeaders } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [recordFor, setRecordFor] = useState<string | null>(null);
  // Inline video edit drawer — one instance for the phase; the episode id being edited.
  const [editFor, setEditFor] = useState<string | null>(null);
  const [pickerFor, setPickerFor] = useState<string | null>(null);
  const [projectFiles, setProjectFiles] = useState<ProjectFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [shootKit, setShootKit] = useState<SeriesEpisode | null>(null);

  // Publish config
  const [priceInput, setPriceInput] = useState('4.99');
  const [pricingMode, setPricingMode] = useState<'subscription' | 'one_time'>('subscription');
  const [weekly, setWeekly] = useState(true);
  const [publishing, setPublishing] = useState(false);

  const episodes = project.episodes;
  const attachedCount = episodes.filter(e => (e.s3Key || '').trim()).length;

  const updateEp = (id: string, u: Partial<SeriesEpisode>) => patch({ episodes: project.episodes.map(e => e.id === id ? { ...e, ...u } : e) });

  const loadFiles = useCallback(async () => {
    if (projectFiles.length) return;
    setLoadingFiles(true);
    try {
      const res = await fetch('/api/projects', { headers: getAuthHeaders() });
      if (res.ok) {
        const { projects } = await res.json();
        const files: ProjectFile[] = [];
        for (const proj of projects || []) {
          for (const f of proj.files || []) if (f.fileType === 'video' && f.s3Key) files.push({ id: f.id || f.s3Key, name: f.name || 'Untitled', s3Key: f.s3Key, duration: f.duration, thumbnailUrl: f.thumbnailUrl });
          for (const r of proj.renders || []) if (r.savedS3Key) files.push({ id: r.id || r.savedS3Key, name: r.name || 'Rendered', s3Key: r.savedS3Key, duration: r.duration, thumbnailUrl: r.thumbnailUrl });
        }
        setProjectFiles(files);
      }
    } catch { /* non-fatal */ } finally { setLoadingFiles(false); }
  }, [projectFiles.length, getAuthHeaders]);

  // Upload a raw file + fire a source-only Mux ingest (mirrors the lesson path).
  const uploadEpisodeFile = async (file: File): Promise<Partial<SeriesEpisode>> => {
    const { uploadUrl, key, fileType } = await getProductFileUploadUrl(file.name, file.type);
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.addEventListener('load', () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error('Upload failed')));
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.open('PUT', uploadUrl); xhr.setRequestHeader('Content-Type', file.type); xhr.send(file);
    });
    let mux = null;
    if (fileType === 'video') mux = await requestMuxIngest({ s3Key: key, kind: 'paid' }).catch(() => null);
    return { s3Key: key, status: 'shot', muxAssetId: mux?.muxAssetId, muxPlaybackId: mux?.muxPlaybackId || undefined, muxIngestStatus: mux?.muxIngestStatus };
  };

  const handleDirectUpload = async (epId: string, file: File) => {
    setUploadingFor(epId);
    try { updateEp(epId, await uploadEpisodeFile(file)); toast({ title: 'Footage attached' }); }
    catch (err: any) { toast({ title: 'Upload failed', description: err.message, variant: 'destructive' }); }
    finally { setUploadingFor(null); }
  };

  // Record → save directly (no forced editor). Editing is one optional click
  // away via the toast action or the row's "Edit video" button.
  const onRecorded = async (file: File) => {
    const target = recordFor;
    setRecordFor(null);
    if (!target) return;
    setUploadingFor(target);
    try {
      updateEp(target, await uploadEpisodeFile(file));
      toast({
        title: 'Recording saved',
        action: (
          <ToastAction altText="Edit video" onClick={() => setEditFor(target)}>
            Edit video
          </ToastAction>
        ),
      });
    } catch (err: any) {
      toast({ title: 'Upload failed', description: err.message, variant: 'destructive' });
    } finally { setUploadingFor(null); }
  };

  const attachFromProject = (epId: string, pf: ProjectFile) => {
    updateEp(epId, { s3Key: pf.s3Key, thumbnailUrl: pf.thumbnailUrl, duration: pf.duration, status: 'shot' });
    setPickerFor(null);
  };

  const publish = async () => {
    const ready = episodes.filter(e => (e.s3Key || '').trim());
    if (!ready.length) { toast({ title: 'Attach at least one episode first', variant: 'destructive' }); return; }
    const priceCents = Math.round(parseFloat(priceInput) * 100);
    if (isNaN(priceCents) || priceCents < 0 || (priceCents > 0 && priceCents < 100)) { toast({ title: 'Enter $0 (free) or at least $1.00', variant: 'destructive' }); return; }
    setPublishing(true);
    try {
      const videos: Partial<ProductVideo>[] = ready.map((e, i) => ({
        title: e.title || `Episode ${i + 1}`, s3Key: e.s3Key!, fileType: 'video',
        episodeNumber: i + 1, logline: e.logline,
        dripDay: pricingMode === 'subscription' && weekly ? i * 7 : 0,
        thumbnailUrl: e.thumbnailUrl,
        muxAssetId: e.muxAssetId, muxPlaybackId: e.muxPlaybackId, muxIngestStatus: e.muxIngestStatus,
      }));
      const payload: Partial<CreatorProduct> = {
        title: project.title || 'Untitled series',
        description: project.premise || '',
        price: priceCents,
        productType: 'single', format: 'series',
        pricingMode, published: true,
        videos: videos as ProductVideo[],
      };
      const saved = project.productId
        ? await updateProduct(project.productId, payload)
        : await createProduct(payload);

      // Ingest fan-out for any episode video that isn't on Mux yet.
      const ingests: Promise<unknown>[] = [];
      (saved.videos || []).forEach((v, idx) => {
        if (v.muxAssetId) return;
        ingests.push(requestMuxIngest({ productId: saved._id, videoIndex: idx, s3Key: v.s3Key, kind: 'paid' }).catch(() => null));
      });
      if (ingests.length) await Promise.race([Promise.allSettled(ingests), new Promise(r => setTimeout(r, 4000))]);

      // Persist productId + mark attached episodes on the project.
      const nextProject: Partial<SeriesProject> = {
        ...project,
        productId: saved._id,
        episodes: project.episodes.map(e => (e.s3Key || '').trim() ? { ...e, status: 'attached' as const } : e),
      };
      patch({ productId: saved._id, episodes: nextProject.episodes });
      await onPersist(projectId, nextProject);

      toast({ title: project.productId ? 'Series updated' : 'Series published' });
      navigate(`/course/${saved._id}`);
    } catch (err: any) {
      toast({ title: 'Publish failed', description: err.message, variant: 'destructive' });
    } finally { setPublishing(false); }
  };

  if (!episodes.length) {
    return <div className="text-center" style={{ padding: '48px 24px', borderRadius: 'var(--hj-r-md)', border: '1.5px dashed var(--hj-hairline)', color: 'var(--hj-ink)', fontSize: 14 }}>Plan and write some episodes first.</div>;
  }

  return (
    <div className="flex flex-col lg:flex-row gap-6 lg:items-start">
      {/* Episodes → footage */}
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        <h2 className="font-hj-display m-0" style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', marginBottom: 6 }}>Shoot & attach</h2>
        {episodes.map(e => {
          const attached = !!(e.s3Key || '').trim();
          return (
            <div key={e.id} data-paper="beige" style={{ padding: 12, borderRadius: 'var(--hj-r-md)', background: 'var(--hj-beige)', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)' }}>
              <div className="flex items-center gap-3">
                <span className="tabular flex-shrink-0" style={{ fontFamily: 'var(--hj-font-mono)', fontSize: 12, color: 'var(--hj-beige-ink-3)' }}>Ep {e.number}</span>
                <span className="flex-1 truncate" style={{ fontSize: 13.5, fontWeight: 600 }}>{e.title || 'Untitled'}</span>
                <StatusChip status={e.status} scripted={!!(e.script || '').trim()} />
                <button type="button" onClick={() => setShootKit(e)} className="inline-flex items-center gap-1 flex-shrink-0" title="Open the shoot kit (script + shots) on your phone" style={{ padding: '5px 10px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-3)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}><Smartphone className="w-3.5 h-3.5" /> Shoot kit</button>
              </div>
              <div className="flex items-center gap-2 flex-wrap" style={{ marginTop: 10 }}>
                {attached ? (
                  <>
                    <span className="inline-flex items-center gap-1.5" style={{ fontSize: 12.5, color: '#5a8a2e', fontWeight: 600 }}><Check className="w-4 h-4" /> Footage attached{e.muxIngestStatus ? ` · ${e.muxIngestStatus}` : ''}</span>
                    <button type="button" onClick={() => setEditFor(e.id!)} className="inline-flex items-center gap-1" style={{ padding: '5px 10px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><Video className="w-3.5 h-3.5" /> Edit video</button>
                    <button type="button" onClick={() => updateEp(e.id!, { s3Key: '', muxAssetId: undefined, muxPlaybackId: undefined, muxIngestStatus: undefined, status: (e.script || '').trim() ? 'scripted' : 'planned' })} style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)', background: 'none', border: 0, cursor: 'pointer', textDecoration: 'underline' }}>Replace</button>
                  </>
                ) : (
                  <>
                    <button type="button" onClick={() => setRecordFor(e.id!)} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}><Camera className="w-3.5 h-3.5" /> Record</button>
                    <label className="cursor-pointer inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600 }}>
                      {uploadingFor === e.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload
                      <input type="file" accept="video/*" className="hidden" onChange={ev => { const f = ev.target.files?.[0]; if (f) handleDirectUpload(e.id!, f); ev.target.value = ''; }} />
                    </label>
                    <button type="button" onClick={() => { setPickerFor(e.id!); loadFiles(); }} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><FolderOpen className="w-3.5 h-3.5" /> From projects</button>
                  </>
                )}
              </div>
              {pickerFor === e.id && (
                <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: 'var(--hj-beige-hover)', border: '1px solid var(--hj-beige-line)' }}>
                  {loadingFiles ? (
                    <div className="flex items-center gap-2 py-3 justify-center" style={{ color: 'var(--hj-beige-ink-3)' }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
                  ) : projectFiles.length === 0 ? (
                    <p className="text-center py-3 m-0" style={{ fontSize: 12.5, color: 'var(--hj-beige-ink-3)' }}>No videos in your projects.</p>
                  ) : (
                    <div className="overflow-y-auto flex flex-col gap-1" style={{ maxHeight: 180 }}>
                      {projectFiles.map(f => (
                        <button key={f.id} type="button" onClick={() => attachFromProject(e.id!, f)} className="w-full flex items-center gap-2 text-left" style={{ padding: 7, borderRadius: 6, background: '#fff', border: '1px solid var(--hj-beige-line)', cursor: 'pointer' }}>
                          <Video className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--hj-beige-ink-3)' }} />
                          <span className="flex-1 truncate" style={{ fontSize: 12.5 }}>{f.name}</span>
                          <Plus className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-3)' }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Publish panel */}
      <div className="w-full lg:w-80 lg:flex-shrink-0">
        <BuilderCard kicker="Publish">
          <p className="m-0" style={builderHelpStyle}>{attachedCount}/{episodes.length} episodes have footage. Publishing creates your marketplace listing.</p>
          <div>
            <Label style={builderLabelStyle}>Pricing</Label>
            <Select value={pricingMode} onValueChange={(v: 'subscription' | 'one_time') => setPricingMode(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="subscription">Monthly subscription</SelectItem>
                <SelectItem value="one_time">One-time purchase</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label style={builderLabelStyle}>Price (USD){pricingMode === 'subscription' ? ' /mo' : ''}</Label>
            <Input type="number" min="0" step="0.01" value={priceInput} onChange={e => setPriceInput(e.target.value)} />
          </div>
          {pricingMode === 'subscription' && (
            <div className="flex items-center justify-between">
              <div>
                <Label style={{ ...builderLabelStyle, marginBottom: 2 }}>Release weekly</Label>
                <p className="m-0" style={{ fontSize: 11.5, color: 'var(--hj-beige-ink-3)' }}>Ep 1 now, then one per week.</p>
              </div>
              <Switch checked={weekly} onCheckedChange={setWeekly} />
            </div>
          )}
          <button type="button" onClick={publish} disabled={publishing || !attachedCount} className="inline-flex items-center gap-1.5 justify-center w-full" style={{ padding: '11px 16px', borderRadius: 999, background: 'var(--hj-blue)', border: '1px solid var(--hj-blue)', color: 'oklch(0.18 0.005 70)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', opacity: (publishing || !attachedCount) ? 0.6 : 1, minHeight: 44 }}>
            {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} {project.productId ? 'Update listing' : 'Publish series'}
          </button>
          {project.productId && <button type="button" onClick={() => navigate(`/course/${project.productId}`)} className="inline-flex items-center gap-1 justify-center w-full" style={{ fontSize: 12, color: 'var(--hj-beige-ink-3)', background: 'none', border: 0, cursor: 'pointer' }}>View listing <ArrowRight className="w-3.5 h-3.5" /></button>}
        </BuilderCard>
      </div>

      {/* Record modal — saves directly; editing is optional afterwards */}
      <RecordingModal open={!!recordFor} onClose={() => setRecordFor(null)} onSave={onRecorded} initialScript={recordFor ? (episodes.find(e => e.id === recordFor)?.script || undefined) : undefined} />

      {/* Inline video edit drawer (single phase-level instance) */}
      {(() => {
        const editEp = editFor ? episodes.find(e => e.id === editFor) : null;
        if (!editEp || !(editEp.s3Key || '').trim()) return null;
        return (
          <VideoEditDrawer
            open
            video={{ s3Key: editEp.s3Key!, title: editEp.title || `Episode ${editEp.number}`, muxPlaybackId: editEp.muxPlaybackId }}
            onApplied={(p) => updateEp(editEp.id!, { s3Key: p.s3Key, muxAssetId: p.muxAssetId, muxPlaybackId: p.muxPlaybackId || undefined, muxIngestStatus: p.muxIngestStatus, thumbnailUrl: undefined, duration: undefined })}
            onClose={() => setEditFor(null)}
          />
        );
      })()}

      {/* Shoot kit — phone-readable script + shots + prep */}
      {shootKit && <ShootKit project={project} episode={shootKit} onClose={() => setShootKit(null)} />}
    </div>
  );
}

function ShootKit({ project, episode, onClose }: { project: SeriesProject; episode: SeriesEpisode; onClose: () => void }) {
  const shootUrl = typeof window !== 'undefined' ? `${window.location.origin}/my-store/series-studio/${project._id}` : '';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto" style={{ background: 'rgb(0 0 0 / 0.5)', padding: '24px 12px' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="w-full" style={{ maxWidth: 460, background: 'var(--hj-beige)', color: 'var(--hj-beige-ink)', borderRadius: 'var(--hj-r-lg)', padding: 20 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)' }}>Shoot kit · Ep {episode.number}</div>
          <button type="button" onClick={onClose} aria-label="Close" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--hj-beige-ink-3)' }}><X className="w-5 h-5" /></button>
        </div>
        <h2 className="font-hj-display m-0" style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>{episode.title || 'Untitled'}</h2>
        {episode.logline && <p className="m-0" style={{ marginTop: 4, fontSize: 13.5, color: 'var(--hj-beige-ink-2)' }}>{episode.logline}</p>}

        {(episode.shotList?.length || 0) > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 6 }}>Shots</div>
            <ul className="m-0" style={{ paddingLeft: 18, fontSize: 13.5, lineHeight: 1.5 }}>
              {episode.shotList!.map((s, i) => <li key={i} style={{ marginBottom: 4 }}><strong>{s.framing}</strong> — {s.shot}{s.note ? ` (${s.note})` : ''}</li>)}
            </ul>
          </div>
        )}

        <div style={{ marginTop: 16 }}>
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 6 }}>Script</div>
          <div style={{ whiteSpace: 'pre-wrap', fontSize: 16, lineHeight: 1.65, fontFamily: 'var(--hj-font-mono)' }}>{episode.script?.trim() || 'No script yet — write it in the Write step.'}</div>
        </div>

        {(project.assets?.length || 0) > 0 && (
          <div style={{ marginTop: 16 }}>
            <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 6 }}>Bring</div>
            <ul className="m-0" style={{ paddingLeft: 18, fontSize: 13.5, lineHeight: 1.5 }}>
              {project.assets.filter(a => !a.done).map(a => <li key={a.id}>{a.label}</li>)}
            </ul>
          </div>
        )}

        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--hj-beige-line)' }}>
          <div className="mono-label" style={{ color: 'var(--hj-beige-ink-3)', marginBottom: 6 }}>Shooting on your phone?</div>
          <button type="button" onClick={() => { navigator.clipboard?.writeText(shootUrl); }} className="inline-flex items-center gap-1.5" style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}><Smartphone className="w-3.5 h-3.5" /> Copy studio link</button>
        </div>
      </div>
    </div>
  );
}

// ============================ Shared bits ============================
function StatusChip({ status, scripted }: { status: SeriesEpisode['status']; scripted: boolean }) {
  const label = status === 'planned' && scripted ? 'scripted' : status;
  return <span className="mono-label flex-shrink-0" style={{ padding: '2px 8px', borderRadius: 999, background: 'var(--hj-beige-deep)', color: 'var(--hj-beige-ink-3)', fontSize: 10 }}>{label}</span>;
}

function StringList({ items, onChange, placeholder }: { items: string[]; onChange: (v: string[]) => void; placeholder: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input value={it} onChange={e => onChange(items.map((x, idx) => idx === i ? e.target.value : x))} placeholder={placeholder} className="h-9" />
          <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} aria-label="Remove" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))' }}><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...items, ''])} className="inline-flex items-center gap-1 self-start" style={{ padding: '5px 10px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-3)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}><Plus className="w-3 h-3" /> Add</button>
    </div>
  );
}

function EditableList<T extends { id?: string }>({ icon: Icon, title, items, onChange, blank, render }: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  title: string; items: T[]; onChange: (v: T[]) => void; blank: () => T;
  render: (item: T, update: (u: Partial<T>) => void) => React.ReactNode;
}) {
  return (
    <BuilderCard>
      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4" style={{ color: 'var(--hj-beige-ink-3)' }} />
          <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--hj-beige-ink)' }}>{title}</span>
        </div>
        <button type="button" onClick={() => onChange([...items, blank()])} className="inline-flex items-center gap-1" style={{ padding: '5px 10px', borderRadius: 999, background: 'transparent', border: '1px solid var(--hj-beige-line)', color: 'var(--hj-beige-ink-3)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}><Plus className="w-3 h-3" /> Add</button>
      </div>
      {items.length === 0 ? (
        <p className="m-0" style={builderHelpStyle}>None yet.</p>
      ) : (
        <div className="flex flex-col gap-2.5">
          {items.map((item, i) => (
            <div key={item.id || i} className="flex items-start gap-2">
              <div className="flex-1 flex flex-wrap gap-2">
                {render(item, (u) => onChange(items.map((x, idx) => idx === i ? { ...x, ...u } : x)))}
              </div>
              <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} aria-label="Remove" style={{ background: 'none', border: 0, cursor: 'pointer', color: 'hsl(var(--destructive))', marginTop: 6 }}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </BuilderCard>
  );
}

export default SeriesStudioPage;
